import { useMemo } from 'react';
import { NavSectionLeaf } from '@tanium/react-workbench';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { Icon, CloudIcon, RegistryIcon, AdminIcon } from '@tanium/react-graphics';
import { SideNavType } from './components/SideNav/types';
import { useTenant } from './hooks/useTenant';
import { useHasPermissions } from './hooks/useUserPermissions';
import { Permission } from './models/Permissions';
import { useTaniumInstances } from './hooks/useTaniumInstances';
import { isOnPremLicenseDownloadable } from './utilities/instanceHelper';

export interface NavRoute extends NavSectionLeaf {
  routeType: SideNavType;
}

// Exported so the Tanium Instances table's View Licenses action and the nav entry below cannot drift
// apart.
export const LICENSES_ROUTE_URL = '/licenses';

interface Routes {
  WORKSPACE: { sectionName: string; pageName: string; url: string; icon: Icon };
  ROUTES: NavSectionLeaf[];
}

// getHomeRoutes places "Case Management" below "Account History" and above "Other
// Services". Visible only when both conditions CaseManagementSection itself re-checks
// before rendering are true (tenant flag + GetCaseGrants) -- keeping the two gates
// computed the same way means a future change to the permission matrix can't leave a
// dead nav entry pointing at a page that refuses to render. Only Root/SSO Admin can
// manage grants (see CaseManagementSection, which gates the add/edit/remove controls
// on ManageCaseGrants separately).
const getHomeRoutes = (
  t: TFunction,
  caseManagementEnabled: boolean,
  hasCaseGrantsPermission: boolean,
  hasDownloadableLicenses: boolean,
): NavRoute[] => {
  const MANAGE_ACCOUNT_ROUTE: NavRoute = {
    name: t('Account Management'),
    route: 'account-management',
    url: '/',
    routeType: SideNavType.Home,
  };
  const TENANT_HISTORY_ROUTE: NavRoute = {
    name: t('Account History'),
    route: 'account-history',
    url: '/account-history',
    routeType: SideNavType.Home,
  };

  const CASE_MANAGEMENT_ROUTE: NavRoute = {
    name: t('Case Management'),
    route: 'case-management',
    url: '/case-management',
    routeType: SideNavType.Home,
  };

  const LICENSES_ROUTE: NavRoute = {
    name: t('Licenses'),
    route: 'licenses',
    url: LICENSES_ROUTE_URL,
    routeType: SideNavType.Home,
  };

  const OTHER_SERVICES_ROUTE: NavRoute = {
    name: t('Other Services'),
    route: 'other-services',
    url: '/other-services',
    routeType: SideNavType.Home,
  };

  const routes = [MANAGE_ACCOUNT_ROUTE, TENANT_HISTORY_ROUTE];
  if (caseManagementEnabled && hasCaseGrantsPermission) {
    routes.push(CASE_MANAGEMENT_ROUTE);
  }
  // Cloud-only accounts have nothing to download, so the tab would only ever be empty for them.
  if (hasDownloadableLicenses) {
    routes.push(LICENSES_ROUTE);
  }
  routes.push(OTHER_SERVICES_ROUTE);
  return routes;
};

export enum CloudRoutes {
  Overview = 'instance',
  Administration = 'admin',
  EnvironmentStatus = 'environment-status',
  EventHistory = 'event-history',
  NetworkEgress = 'egress-allow-list',
};

export const getCloudInstanceRoutes = (t: TFunction): { [key in CloudRoutes]: NavRoute } => {
  const INSTANCE_OVERVIEW_ROUTE: NavRoute = {
    name: t('Overview'),
    route: CloudRoutes.Overview,
    url: '/instance/overview',
    routeType: SideNavType.Cloud,
  };
  const ADMINISTRATION_ROUTE: NavRoute = {
    name: t('Administration'),
    route: CloudRoutes.Administration,
    url: '/instance/admin',
    routeType: SideNavType.Cloud,
  };
  const ENVIRONMENT_STATUS_ROUTE: NavRoute = {
    name: t('Environment Status'),
    route: CloudRoutes.EnvironmentStatus,
    url: '/instance/environment-status',
    routeType: SideNavType.Cloud,
  };
  const EVENT_HISTORY_ROUTE: NavRoute = {
    name: t('Event History'),
    route: CloudRoutes.EventHistory,
    url: '/instance/event-history',
    routeType: SideNavType.Cloud,
  };
  const NETWORK_EGRESS_ROUTE: NavRoute = {
    name: t('Network Egress'),
    route: CloudRoutes.NetworkEgress,
    url: '/instance/egress-allow-list',
    routeType: SideNavType.Cloud,
  };

  return {
    [CloudRoutes.Overview]: INSTANCE_OVERVIEW_ROUTE,
    [CloudRoutes.Administration]: ADMINISTRATION_ROUTE,
    [CloudRoutes.EnvironmentStatus]: ENVIRONMENT_STATUS_ROUTE,
    [CloudRoutes.EventHistory]: EVENT_HISTORY_ROUTE,
    [CloudRoutes.NetworkEgress]: NETWORK_EGRESS_ROUTE,
  };
};

const getArtifactsRoutes = (t: TFunction): NavRoute[] => {
  const overview: NavRoute = {
    name: t('Overview'),
    route: 'overview',
    url: '/artifacts/overview',
    routeType: SideNavType.Artifacts,
  };
  return [overview];
};

export const useRoutes = (): Routes => {
  const { t } = useTranslation();
  const location = useLocation();
  const { tenant } = useTenant();
  const hasCaseGrantsPermission = useHasPermissions([Permission.GetCaseGrants]);
  // Optional chaining: useRoutes is also rendered outside TaniumInstancesProvider.
  const { linkedTaniumInstances } = useTaniumInstances();
  const hasDownloadableLicenses = Boolean(linkedTaniumInstances?.some(isOnPremLicenseDownloadable));
  const ampRoutes = getHomeRoutes(
    t,
    !!tenant?.caseManagementEnabled,
    hasCaseGrantsPermission,
    hasDownloadableLicenses,
  );
  const cloudInstanceRoutes = getCloudInstanceRoutes(t);
  const cloudRoutes = Object.values(cloudInstanceRoutes);
  const artifactsRoutes = getArtifactsRoutes(t);

  const currNavRoute = useMemo((): NavRoute | undefined => {
    const { pathname } = location;
    const routes = [...cloudRoutes, ...artifactsRoutes, ...ampRoutes];
    const currRoute = routes.find((route) => pathname.toLowerCase() === route.url.toString());
    return currRoute;
  }, [ampRoutes, artifactsRoutes, cloudRoutes, location]);

  const currPageName = currNavRoute?.name || '';

  let sectionName: string;
  let icon: Icon;
  let url: string;
  let routes: NavSectionLeaf[];
  switch (currNavRoute?.routeType) {
    case SideNavType.Artifacts:
      sectionName = t('Artifacts');
      url = '/artifacts';
      icon = RegistryIcon;
      routes = artifactsRoutes;
      break;
    case SideNavType.Cloud:
      sectionName = t('Cloud Instances');
      url = '/instance/overview';
      icon = CloudIcon;
      routes = cloudRoutes;
      break;
    default:
      sectionName = t('Tanium Account');
      icon = AdminIcon;
      url = '/';
      routes = ampRoutes;
      break;
  }

  return {
    WORKSPACE: { sectionName, pageName: currPageName, url, icon },
    ROUTES: routes,
  };
};
